import React, { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { apiService, setAuthCredentials, AppSetup } from '../services/api'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'

interface SetupForm {
  password: string
  confirmPassword: string
  anthropic_api_key: string
}

const SetupPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)
  
  const queryClient = useQueryClient()
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SetupForm>()

  const password = watch('password')

  const setupMutation = useMutation({
    mutationFn: (data: { password: string; anthropic_api_key: string }) =>
      apiService.completeSetup(data),
    onSuccess: (result, variables) => {
      toast.success('Setup completed successfully!')
      // Set auth credentials
      setAuthCredentials(variables.password)
      // Invalidate setup status to trigger re-render
      queryClient.invalidateQueries({ queryKey: ['setupStatus'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Setup failed'
      toast.error(message)
    },
  })

  const onSubmit = (data: SetupForm) => {
    if (data.password !== data.confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    setupMutation.mutate({
      password: data.password,
      anthropic_api_key: data.anthropic_api_key,
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">SplitScan</h1>
          <p className="text-gray-600">Welcome! Let's set up your shared access.</p>
        </div>

        <div className="bg-white shadow-lg rounded-lg p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Confirm password"
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) =>
                      value === password || 'Passwords do not match',
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Anthropic API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Anthropic API Key</label>
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="sk-..."
                  {...register('anthropic_api_key', {
                    required: 'API key is required',
                    pattern: {
                      value: /^sk-/,
                      message: 'API key must start with "sk-"',
                    },
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.anthropic_api_key && (
                <p className="mt-1 text-sm text-red-600">{errors.anthropic_api_key.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Get your API key from{' '}
                <a
                  href="https://console.anthropic.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700"
                >
                  console.anthropic.com
                </a>
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={setupMutation.isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {setupMutation.isLoading ? 'Setting up...' : 'Complete Setup'}
            </button>
          </form>
        </div>

        <div className="text-center mt-6 text-sm text-gray-500">
          <p>This is a one-time setup. Your credentials will be securely stored.</p>
        </div>
      </div>
    </div>
  )
}

export default SetupPage